import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import { Icon } from '@iconify/react';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { useResponsive } from 'src/hooks/use-responsive';
import { useConfig } from 'src/context/ConfigContext';
// import { bgGradient } from 'src/theme/css';
import { useAuthContext } from 'src/auth/hooks';
import { useTranslate } from 'src/locales';
import { getLandingPageHeroText } from 'src/services/service-handlers/PublicService';
import { useBackgroundImages } from 'src/hooks/use-background-images';
// import Logo from 'src/components/logo';
// import EmblaCarousel from './embla-carousel';
import ParkCityLogo from 'src/components/logo/logo_new';
import Carousel from './embla-carousel';

// Helper function to process HTML/text and replace [link name] with clickable links
const processParagraphContent = (
  content: string,
  links: Array<{ name: string; url: string }> = [],
  fallbackConfig?: any
): { isHtml: boolean; processedContent: string | React.ReactNode[] } => {
  if (!content) return { isHtml: false, processedContent: content };
  
  // Check if content contains HTML tags
  const isHtml = /<[a-z][\s\S]*>/i.test(content);
  
  const linkMap: Record<string, string> = {};
  links.forEach((link) => {
    linkMap[link.name.toLowerCase()] = link.url;
  });

  // Fallback to config if link not found
  const getLinkUrl = (linkName: string): string => {
    const lowerName = linkName.toLowerCase();
    if (linkMap[lowerName]) {
      return linkMap[lowerName];
    }
    // Fallback to old config structure
    if (fallbackConfig) {
      if (lowerName.includes('qualification')) {
        return fallbackConfig.QUALIFICATIONS || '#';
      }
      if (lowerName.includes('project')) {
        return fallbackConfig.PROJECT_WEBPAGE || '#';
      }
      if (lowerName.includes('sale')) {
        return fallbackConfig.SALES_WEBPAGE || '#';
      }
    }
    return '#';
  };

  // If HTML, replace [link name] patterns with HTML anchor tags
  if (isHtml) {
    const linkPattern = /\[([^\]]+)\]/g;
    
    // Replace all [link name] patterns with HTML links
    const processedHtml = content.replace(linkPattern, (_match, linkName) => {
      const linkUrl = getLinkUrl(linkName);
      return `<a href="${linkUrl}" target="_blank" style="color: #FFD400; text-decoration: none;">${linkName}</a>`;
    });
    
    return { isHtml: true, processedContent: processedHtml };
  }

  // If plain text, use the original parsing logic
  const parts: React.ReactNode[] = [];
  const linkPattern = /\[([^\]]+)\]/g;
  let lastIndex = 0;
  
  const matches = Array.from(content.matchAll(linkPattern));
  
  matches.forEach((match) => {
    if (match.index !== undefined && match.index > lastIndex) {
      parts.push(content.substring(lastIndex, match.index));
    }

    const linkName = match[1];
    const linkUrl = getLinkUrl(linkName);

    parts.push(
      <Link
        key={`link-${match.index}`}
        target="_blank"
        href={linkUrl}
        color="#FFD400"
      >
        {linkName}
      </Link>
    );

    if (match.index !== undefined) {
      lastIndex = match.index + match[0].length;
    }
  });

  if (lastIndex < content.length) {
    parts.push(content.substring(lastIndex));
  }

  if (parts.length === 0) {
    return { isHtml: false, processedContent: [content] };
  }

  return { isHtml: false, processedContent: parts };
};

// ----------------------------------------------------------------------

// const METHODS = [
//   {
//     id: 'jwt',
//     label: 'Jwt',
//     path: paths.auth.jwt.login,
//     icon: '/assets/icons/auth/ic_jwt.svg',
//   },
//   {
//     id: 'firebase',
//     label: 'Firebase',
//     path: paths.auth.firebase.login,
//     icon: '/assets/icons/auth/ic_firebase.svg',
//   },
//   {
//     id: 'amplify',
//     label: 'Amplify',
//     path: paths.auth.amplify.login,
//     icon: '/assets/icons/auth/ic_amplify.svg',
//   },
//   {
//     id: 'auth0',
//     label: 'Auth0',
//     path: paths.auth.auth0.login,
//     icon: '/assets/icons/auth/ic_auth0.svg',
//   },
//   {
//     id: 'supabase',
//     label: 'Supabase',
//     path: paths.auth.supabase.login,
//     icon: '/assets/icons/auth/ic_supabase.svg',
//   },
// ];

export type AuthClassicLayoutProps = {
  title?: string;
  image?: string;
  children: React.ReactNode;
};

export function AuthClassicLayout({ children, image, title }: AuthClassicLayoutProps) {
  // const { method } = useAuthContext();
  const { configData } = useConfig();

  // console.log(configData, 'configData');

  const { t } = useTranslate('sign-in');

  const theme = useTheme();
  const { currentLang } = useTranslate();
  const mdUp = useResponsive('up', 'md');

  // Fetch dynamic hero text from admin configurations (by client-selected language)
  const [heroText, setHeroText] = useState<any>(null);
  // Fetch dynamic background images from admin configurations with caching
  const { bgImages, isLoading: bgImagesLoading } = useBackgroundImages();
  
  useEffect(() => {
    const fetchHeroText = async () => {
      try {
        const lang = currentLang?.value === 'es' ? 'es' : 'en';
        const response = await getLandingPageHeroText(lang);
        if (response?.configValue) {
          setHeroText(response.configValue);
        }
      } catch (error) {
        console.error('Error fetching hero text:', error);
      }
    };
    fetchHeroText();
  }, [currentLang?.value]);


  // Helper function to split HTML content into paragraphs
  const splitHtmlIntoParagraphs = (htmlContent: string): string[] => {
    if (!htmlContent) return [];
    
    // Split by <p> tags (handles both opening and closing tags)
    const paragraphRegex = /<p[^>]*>(.*?)<\/p>/gi;
    const paragraphs: string[] = [];
    
    // Use matchAll to avoid assignment in while condition
    const matches = Array.from(htmlContent.matchAll(paragraphRegex));
    matches.forEach((match) => {
      if (match[1] && match[1].trim()) {
        paragraphs.push(`<p>${match[1]}</p>`);
      }
    });
    
    // If no <p> tags found, treat entire content as one paragraph
    if (paragraphs.length === 0 && htmlContent.trim()) {
      paragraphs.push(htmlContent);
    }
    
    return paragraphs;
  };

  // Get hero paragraphs - support both new single field and old para1-para4 format
  const getHeroParagraphs = (): string[] => {
    if (heroText?.hero_text_content) {
      // New format: single HTML field, split by <p> tags
      return splitHtmlIntoParagraphs(heroText.hero_text_content);
    }
    if (heroText?.para1 || heroText?.para2 || heroText?.para3 || heroText?.para4) {
      // Old format: separate para1-para4 fields
      return [
        heroText.para1 || '',
        heroText.para2 || '',
        heroText.para3 || '',
        heroText.para4 || ''
      ].filter(p => p);
    }
    // Fallback to translations
    return [
      t('carousel.slide2.para1'),
      t('carousel.slide2.para2'),
      t('carousel.slide2.para3'),
      t('carousel.slide2.para4')
    ].filter(p => p);
  };

  const renderLogo = (
    <Box
      sx={{
        position: 'absolute',
        top: '10px',
        left: '10px',
        height: '3rem',
        zIndex: 10,
      }}
    >
      <ParkCityLogo />
    </Box>
  );

  const slides = [
    {
      component: (
        <Box>
          <Typography
            sx={{
              fontSize: '4vw',
              fontWeight: 'light',
              color: '#FFFFFF',
            }}
          >
            {t('carousel.slide1.line1')}{' '}
            <Typography
              sx={{
                fontSize: '4vw',
                fontWeight: '700',
                color: '#FFFFFF',
              }}
            >
              {t('carousel.slide1.line2')}{' '}
            </Typography>
            {t('carousel.slide1.line3')}
          </Typography>
        </Box>
      ),
      scrollOnLearnMore: true,
      learnMoreContent: t('carousel.slide1.learnMore'),
    },
    {
      component: (
        <Box
          maxWidth="35vw"
          sx={{
            // bgcolor: { md: 'green', lg: 'blue' },
            display: 'flex',
            flexDirection: 'column',
            gap: '2.5em',
            borderLeftColor: '#FFFFFF',
            borderLeftWidth: '3px',
            borderLeftStyle: 'solid',
            padding: '2.5em',
            color: '#FFFFFF',
            backdropFilter: 'blur(30px)',
            borderRadius: '20px',
            maxHeight: '70vh',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(240, 239, 244, 0.1)',
              borderRadius: '10px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(240, 239, 244, 0.3)',
              borderRadius: '10px',
              '&:hover': {
                background: 'rgba(240, 239, 244, 0.5)',
              }
            }
          }}
        >
          {getHeroParagraphs().map((paragraph, index) => {
            // Since rich text editor handles links directly, just render HTML
            // But still support [link name] placeholder for backward compatibility
            const processedContent = processParagraphContent(
              paragraph,
              [], // No separate links array needed - links are in HTML
              configData?.app_config
            );
            return processedContent.isHtml ? (
              <Typography 
                key={index}
                fontSize="1.302vw"
                dangerouslySetInnerHTML={{ __html: processedContent.processedContent as string }}
                sx={{
                  mb: 0,
                  '& p': {
                    margin: 0,
                    marginBottom: 0,
                    lineHeight: 1.6,
                    '&:last-child': {
                      marginBottom: 0
                    }
                  },
                  '& a': {
                    color: '#FFD400 !important',
                    textDecoration: 'none !important',
                    '&:hover': {
                      textDecoration: 'underline !important'
                    }
                  }
                }}
              />
            ) : (
              <Typography 
                key={index}
                fontSize="1.302vw"
                sx={{ mb: 0, lineHeight: 1.6 }}
              >
                {processedContent.processedContent}
          </Typography>
            );
          })}
        </Box>
      ),
      // backgroundImage: 'https://via.placeholder.com/400x600/ff0000',
    },
  ];

  const renderContent = (
    <Stack
      sx={{
        bgcolor: theme.palette.background.paper,
        width: 1,
        mx: 'auto',
        maxWidth: 480,
        px: { xs: 2, md: 8 },
        // pt: { xs: 15, md: 20 },
        pb: { xs: 15, md: 0 },
      }}
    >
      {children}
    </Stack>
  );

  const renderSection = (
    <Stack
      flexGrow={1}
      spacing={10}
      alignItems="flex-start"
      justifyContent="center"
      sx={{
        position: 'relative',
        // ...bgGradient({
        //   color: alpha(
        //     theme.palette.background.default,
        //     theme.palette.mode === 'light' ? 0.88 : 0.94
        //   ),
        //   imgUrl: '/assets/background/overlay_2.jpg',
        // }),
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url('${bgImages?.bg_image || '/assets/background/mountains_bg_light.png'}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          filter: theme.palette.mode === 'dark' ? 'brightness(0.4)' : 'none',
          zIndex: 0,
        },
      }}
    >
      {/* <Typography variant="h3" sx={{ maxWidth: 480, textAlign: 'left' }}>
        {title || 'Hi, Welcome back'}
      </Typography> */}

      {/* <Box
        component="img"
        alt="auth"
        src={image || '/assets/illustrations/illustration_dashboard.png'}
        sx={{
          maxWidth: {
            xs: 480,
            lg: 560,
            xl: 720,
          },
        }}
      /> */}

      <Box width="100%" height="100vh" sx={{ position: 'relative', zIndex: 1 }}>
        <Carousel slides={slides} />
      </Box>

      {/* <Stack direction="row" spacing={2}>
        {METHODS.map((option) => (
          <Tooltip key={option.label} title={option.label}>
            <Link component={RouterLink} href={option.path}>
              <Box
                component="img"
                alt={option.label}
                src={option.icon}
                sx={{
                  width: 32,
                  height: 32,
                  ...(method !== option.id && {
                    filter: 'grayscale(100%)',
                  }),
                }}
              />
            </Link>
          </Tooltip>
        ))}
      </Stack> */}
    </Stack>
  );

  return (
    <Stack
      component="main"
      direction="row"
      sx={{
        minHeight: '100vh',
      }}
    >
      {renderLogo}

      {mdUp && renderSection}

      {renderContent}
    </Stack>
  );
}
